'use client';

/**
 * Module-private internals. Defaults, the React context, small
 * shared helpers. Not exported from index.js — the host should
 * not need any of this.
 */

import { createContext, useContext } from "react";

export const TeamContext = createContext(null);
export const useTeam = () => {
  const v = useContext(TeamContext);
  if (!v) throw new Error("useTeam used outside <TeamModule>.");
  return v;
};

export const defaultCostume = {
  // Which roles are exposed in the Invite drawer + the role chip
  // legend. Only the agreed two-role model is assignable: Owner/Admin and
  // Staff (8 Aug 2026 — old roles still display on legacy members but can
  // never be assigned again).
  visibleRoles: ["admin", "staff"],
  // Whether to show the Teams tab (multi-team plan).
  enableTeams: false,
  // Whether to show the Hubs tab (multi-branch plan).
  enableHubs: false,
};

export const defaultCopy = {
  // Plain English, edited if the host wants industry-flavoured copy.
  page_title: "Team",
  page_subtitle: "Manage who has access to this workspace.",
  tab_all: "All members",
  tab_owners: "Owners",
  tab_pending: "Pending",
  empty_title: "No team members yet",
  empty_body: "Invite the first person to your team to get started.",
  cta_invite: "Invite member",
  role_owner: "Owner",
  role_admin: "Owner / Admin",
  role_manager: "Manager",
  role_staff: "Staff",
  role_field_worker: "Field worker",
  status_active: "Active",
  status_on_leave: "On leave",
  status_resigned: "Resigned",
  status_inactive: "Inactive",
  tab_activity: "Activity",
  role_desc_owner: "Full control. Can manage everyone and hand over ownership.",
  role_desc_admin: "Full control — team, settings and every enquiry.",
  role_desc_manager: "Leads a team and can invite staff.",
  role_desc_staff: "Sees and works only their own enquiries.",
  role_desc_field_worker: "Works on the move using a phone.",
};

// ----- small helpers (no host dependencies) -----

export const initialsOf = (name) => {
  const parts = String(name || "").trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return "·";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const formatPhone = (raw) => {
  const d = String(raw || "").replace(/\D/g, "");
  if (d.length !== 10) return raw || "—";
  return `${d.slice(0, 5)} ${d.slice(5)}`;
};

export const errText = (e) =>
  e?.response?.data?.detail || e?.message || "Something went wrong. Please try again.";

export const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "on_leave", label: "On leave" },
  { value: "resigned", label: "Resigned" },
  { value: "inactive", label: "Inactive" },
];

const ACTION_VERBS = {
  member_created: "added",
  member_updated: "updated",
  member_removed: "removed",
  accepted: "joined the team",
  ownership_transferred: "handed ownership to",
  invite_created: "invited",
  invite_revoked: "cancelled the invite for",
  invite_expired: "let the invite expire for",
  modules_set: "changed access for",
  team_created: "created a team",
  team_updated: "updated a team",
  team_deleted: "deleted a team",
  team_assignment_created: "added to a team",
  team_assignment_removed: "removed from a team",
};

export const humanizeAction = (a) => ACTION_VERBS[a] || String(a || "").replace(/_/g, " ");

export const formatWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
};

// The app never messages an invitee automatically (no SMS is sent), so the
// inviter hands over the joining steps themselves — WhatsApp or copy-paste.
export const inviteJoinMessage = ({ name, phone }) => {
  const first = String(name || "").trim().split(" ")[0] || "there";
  return (
    `Hi ${first}! I've added you to our team on Jigyasa — the app we use to track enquiries.\n\n` +
    `Joining takes a minute:\n` +
    `1. Open ${window.location.origin}\n` +
    `2. Tap "Log in", then "Been invited to a team? Join your team"\n` +
    `3. Enter this mobile number: ${phone}\n` +
    `4. Type the SMS code you get, and choose a 4-digit PIN.\n\n` +
    `That's it — you're in. The invite is valid for 7 days.`
  );
};

export const inviteWhatsAppUrl = ({ name, phone }) => {
  const digits = String(phone || "").replace(/\D/g, "").slice(-10);
  return `https://wa.me/91${digits}?text=${encodeURIComponent(inviteJoinMessage({ name, phone: digits }))}`;
};
